import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { showErrorToast, showInfoToast, showSuccessToast } from "../../components/Toast";
import { getAuthToken } from "../../lib/api/tokenStore";
import useProfile from "../profile/useProfile";
import useWallet from "./useWallet";
import useTransactions from "./useTransactions";
import useTransferRecipients from "./useTransferRecipients";
import usePayouts from "./usePayouts";
import { readPendingTopUp } from "../checkout/topUpReturn";
import { referenceFromLocation } from "../checkout/paystackReturn";
import useFinanceTopUp from "./useFinanceTopUp";
import FinanceShell from "./components/FinanceShell";
import WalletCard from "./components/WalletCard";
import RecipientCard from "./components/RecipientCard";
import TransactionTable from "./components/TransactionTable";
import TransactionDetailDialog from "./components/TransactionDetailDialog";
import AmountEntrySheet from "./components/AmountEntrySheet";
import AddRecipientSheet from "./components/AddRecipientSheet";
import FinanceEmptyState from "./components/FinanceEmptyState";
import PopupBlockedDialog from "../checkout/components/PopupBlockedDialog";
import {
  FinancePageSkeleton,
  RecipientCardsSkeleton,
  TransactionTableSkeleton,
} from "./components/FinanceSkeletons";
import { FINANCE_COPY, FINANCE_EMPTY_STATES, serverMessage, toppedUpMessage } from "./financeCopy";
import { TOP_UP_CURRENCY } from "./financeMoney";
import {
  TRANSACTIONS_PATH,
  WITHDRAWALS_PATH,
  orderDetailPath,
  withdrawPath,
} from "./financeRoutes";

import "../checkout/styles.css";
import "./styles.css";

const PREVIEW_SIZE = 10;

const FinanceRoute = () => {
  const navigate = useNavigate();
  const enabled = Boolean(getAuthToken());

  const { profile, loading: profileLoading, fetchProfile } = useProfile();
  const wallet = useWallet({ enabled });
  const recipients = useTransferRecipients({ enabled });
  const payouts = usePayouts({ pageSize: 1, enabled });
  const transactions = useTransactions({ pageSize: PREVIEW_SIZE, enabled });
  const topUp = useFinanceTopUp();

  const [sheet, setSheet] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (enabled && !profile && !profileLoading) fetchProfile();
  }, [enabled, profile, profileLoading, fetchProfile]);

  const closeSheet = () => setSheet(null);

  const refreshAll = () => {
    wallet.refetch();
    recipients.refetch();
    payouts.refetch();
    transactions.refetch();
  };

  const reportTopUp = (outcome) => {
    if (outcome.status === "credited") {
      showSuccessToast(toppedUpMessage(outcome.amount), "Wallet Topped Up");
      refreshAll();

      return;
    }

    if (outcome.status === "pending") {
      showInfoToast(FINANCE_COPY.topUpPending.message, FINANCE_COPY.topUpPending.title);
      refreshAll();
    }
  };

  const settledReturn = useRef(false);

  useEffect(() => {
    if (!enabled || settledReturn.current) return;

    const returned = referenceFromLocation(window.location.search);
    const pending = readPendingTopUp();

    if (!returned && !pending) return;

    settledReturn.current = true;

    const settle = async () => {
      try {
        reportTopUp(
          await topUp.confirm({
            reference: returned || pending.reference,
            amount: pending?.amount ?? null,
            currencyCode: pending?.currencyCode || TOP_UP_CURRENCY,
            balanceBefore: pending?.balanceBefore ?? null,
          })
        );
      } finally {
        if (returned) window.history.replaceState({}, "", window.location.pathname);
      }
    };

    settle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const handleTopUp = async (amount) => {
    closeSheet();

    const outcome = await topUp.start({
      amount,
      currencyCode: wallet.currencyCode,
      email: profile?.email,
    });

    if (outcome.status === "email-required") {
      showErrorToast(FINANCE_COPY.emailRequired.message, FINANCE_COPY.emailRequired.title);

      return;
    }

    if (outcome.status === "wallet-unavailable") {
      showErrorToast(FINANCE_COPY.topUpWalletGone.message, FINANCE_COPY.topUpWalletGone.title);

      return;
    }

    if (outcome.status === "initiate-failed") {
      showErrorToast(
        serverMessage(outcome.error, FINANCE_COPY.topUpFailed.message),
        FINANCE_COPY.topUpFailed.title
      );

      return;
    }

    reportTopUp(outcome);
  };

  const handleResumeTopUp = async () => {
    reportTopUp(await topUp.resumeBlocked());
  };

  const handleWithdraw = (amount) => {
    closeSheet();
    navigate(withdrawPath({ amount, currency: wallet.currencyCode }));
  };

  const handleAddRecipient = async (payload) => {
    const outcome = await recipients.create(payload);

    if (outcome.status === "error") {
      showErrorToast(serverMessage(outcome.error, "Could not save this account"));

      return;
    }

    const copy = payload.type === "BANK" ? FINANCE_COPY.bankSaved : FINANCE_COPY.momoSaved;

    showSuccessToast(copy.message, copy.title);
    closeSheet();
  };

  const handleRemoveRecipient = async (recipient) => {
    const outcome = await recipients.remove(recipient.id);

    if (outcome.status === "error") {
      showErrorToast(serverMessage(outcome.error, FINANCE_COPY.recipientDeleteFailed));
    }
  };

  const firstLoad =
    wallet.isLoading && recipients.isLoading && transactions.isLoading && payouts.isLoading;

  const walletDead = wallet.isError && !wallet.wallet;

  return (
    <FinanceShell
      title="Finance"
      showBack={false}
    >
      {firstLoad ? (
        <FinancePageSkeleton />
      ) : walletDead ? (
        <FinanceEmptyState state={FINANCE_EMPTY_STATES.walletError} onRetry={refreshAll} />
      ) : (
        <>
          <div className="fin-top-grid">
            <WalletCard
              balance={wallet.balance}
              busy={topUp.busy}
              onWithdraw={() => setSheet("withdraw")}
              onTopUp={() => setSheet("top-up")}
            />

            <section className="sy-flow-card">
              <div className="sy-flow-card-head">
                <h2 className="sy-flow-card-title">Withdrawal Accounts</h2>

                <div className="fin-card-actions">
                  <button type="button" className="fin-chip" onClick={() => setSheet("bank")}>
                    + Bank
                  </button>

                  <button type="button" className="fin-chip" onClick={() => setSheet("momo")}>
                    + Mobile Money
                  </button>
                </div>
              </div>

              <div className="sy-flow-card-body">
                {recipients.isLoading ? (
                  <RecipientCardsSkeleton />
                ) : recipients.recipients.length ? (
                  <div className="fin-accounts-rail">
                    {recipients.recipients.map((recipient) => (
                      <RecipientCard
                        key={recipient.id}
                        recipient={recipient}
                        busy={recipients.removingId === recipient.id}
                        onRemove={handleRemoveRecipient}
                      />
                    ))}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="fin-empty-card"
                    onClick={() => setSheet("bank")}
                  >
                    <b>{FINANCE_EMPTY_STATES.noRecipients.title}</b>
                    <span>{FINANCE_EMPTY_STATES.noRecipients.body}</span>
                  </button>
                )}

                {payouts.total > 0 && (
                  <button
                    type="button"
                    className="fin-link-row"
                    onClick={() => navigate(WITHDRAWALS_PATH)}
                  >
                    <span>Withdrawal Requests · {payouts.total}</span>

                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="m9 5 7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>
            </section>
          </div>

          <section className="sy-flow-card">
            <div className="sy-flow-card-head">
              <h2 className="sy-flow-card-title">Transaction History</h2>

              {!transactions.isLoading && !transactions.isError && transactions.items.length > 0 && (
                <button
                  type="button"
                  className="fin-head-link"
                  onClick={() => navigate(TRANSACTIONS_PATH)}
                >
                  <span>
                    {transactions.total > transactions.items.length
                      ? `See All ${transactions.total} Transactions`
                      : "See All Transactions"}
                  </span>

                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="m9 5 7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>

            <div className="sy-flow-card-body">
              {transactions.isLoading ? (
                <TransactionTableSkeleton rows={PREVIEW_SIZE} />
              ) : transactions.isError ? (
                <FinanceEmptyState
                  inline
                  state={FINANCE_EMPTY_STATES.transactionsError}
                  onRetry={transactions.refetch}
                />
              ) : transactions.items.length ? (
                <TransactionTable items={transactions.items} onSelect={setSelected} />
              ) : (
                <FinanceEmptyState inline state={FINANCE_EMPTY_STATES.noTransactions} />
              )}
            </div>
          </section>
        </>
      )}

      <AmountEntrySheet
        open={sheet === "top-up"}
        mode="top-up"
        balance={wallet.balance}
        busy={topUp.busy}
        onClose={closeSheet}
        onSubmit={handleTopUp}
      />

      <AmountEntrySheet
        open={sheet === "withdraw"}
        mode="withdraw-finance"
        balance={wallet.balance}
        maximumAmount={wallet.balance.value}
        onClose={closeSheet}
        onSubmit={handleWithdraw}
      />

      <AddRecipientSheet
        open={sheet === "bank"}
        kind="BANK"
        currency={wallet.currencyCode}
        saving={recipients.saving}
        onClose={closeSheet}
        onSubmit={handleAddRecipient}
      />

      <AddRecipientSheet
        open={sheet === "momo"}
        kind="MOBILE_MONEY"
        currency={wallet.currencyCode}
        saving={recipients.saving}
        onClose={closeSheet}
        onSubmit={handleAddRecipient}
      />

      {topUp.blocked && (
        <PopupBlockedDialog
          busy={topUp.busy}
          onRetry={handleResumeTopUp}
          onCancel={topUp.dismissBlocked}
        />
      )}

      {selected && (
        <TransactionDetailDialog
          transaction={selected}
          onClose={() => setSelected(null)}
          onViewOrder={(orderId) => navigate(orderDetailPath(orderId))}
        />
      )}
    </FinanceShell>
  );
};

export default FinanceRoute;
